import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AdminUser, ApplicationDomainSettings, SuperAdminSetupRequest } from '../dtos/admin-user';
import { Globals } from '../global/globals';

@Injectable({
  providedIn: 'root',
})
export class AdminUserService {
  private readonly usersUri = this.globals.backendUri + '/users';
  private readonly authenticationUri = this.globals.backendUri + '/authentication';
  private readonly systemSettingsUri = this.globals.backendUri + '/system-settings';

  constructor(
    private httpClient: HttpClient,
    private globals: Globals,
  ) {}

  createSuperAdmin(request: SuperAdminSetupRequest): Observable<void> {
    return this.httpClient.post<void>(`${this.authenticationUri}/setup/super-admin`, request);
  }

  isSuperAdminSetupAvailable(): Observable<boolean> {
    return this.httpClient
      .get<{ available: boolean }>(`${this.authenticationUri}/setup/super-admin/status`)
      .pipe(map((status) => !!status.available));
  }

  getApplicationDomain(): Observable<ApplicationDomainSettings> {
    return this.httpClient.get<ApplicationDomainSettings>(
      `${this.systemSettingsUri}/application-domain`,
    );
  }

  updateApplicationDomain(applicationDomain: string): Observable<ApplicationDomainSettings> {
    return this.httpClient.put<ApplicationDomainSettings>(
      `${this.systemSettingsUri}/application-domain`,
      { applicationDomain },
    );
  }

  getUsers(): Observable<AdminUser[]> {
    return this.httpClient
      .get<any[]>(this.usersUri)
      .pipe(map((users) => users.map((user) => this.mapUser(user))));
  }

  assignAdminRole(userId: string): Observable<AdminUser | null> {
    return this.httpClient
      .put<any>(`${this.usersUri}/${encodeURIComponent(userId)}/admin-role`, { admin: true })
      .pipe(map((user) => (user ? this.mapUser(user) : null)));
  }

  removeAdminRole(userId: string): Observable<AdminUser | null> {
    return this.httpClient
      .put<any>(`${this.usersUri}/${encodeURIComponent(userId)}/admin-role`, { admin: false })
      .pipe(map((user) => (user ? this.mapUser(user) : null)));
  }

  transferSuperAdminRole(userId: string, currentPassword: string): Observable<AdminUser | null> {
    return this.httpClient
      .put<any>(`${this.usersUri}/${encodeURIComponent(userId)}/super-admin`, {
        currentPassword,
      })
      .pipe(map((user) => (user ? this.mapUser(user) : null)));
  }

  deleteUser(userId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.usersUri}/${encodeURIComponent(userId)}`);
  }

  reactivateUser(userId: string): Observable<AdminUser | null> {
    return this.httpClient
      .put<any>(`${this.usersUri}/${encodeURIComponent(userId)}/activate`, null)
      .pipe(map((user) => (user ? this.mapUser(user) : null)));
  }

  private mapUser(user: any): AdminUser {
    const roles = Array.isArray(user?.roles) ? user.roles : user?.role ? [user.role] : [];

    return {
      id: String(user?.id ?? ''),
      email: user?.email ?? '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      affiliation: user?.affiliation ?? undefined,
      roles,
      active: user?.active ?? user?.deactivatedAt == null,
      emailVerified: !!user?.emailVerified,
      deactivatedAt: user?.deactivatedAt ?? null,
    };
  }
}
